/**
 * LeadAutoReplyShadowCard — qué habría contestado el bot, y si estuvo bien.
 *
 * Auto Reply lleva escribiendo veredictos en sombra desde que se encendió y
 * no había forma de mirarlos. Esta tarjeta es esa forma: por cada ráfaga, el
 * mensaje que la disparó, la respuesta exacta que el bot habría mandado, el
 * veredicto con sus motivos en español, y un sitio donde decir si estuvo
 * bien.
 *
 * NADA DE AQUÍ ENVÍA. Ni el pulgar, ni la corrección, ni recargar. El
 * backend tampoco puede: hay un test estructural sobre sus dos rutas.
 *
 * EL BORRADOR SE ENSEÑA SOLO SI SE PUEDE DEMOSTRAR
 *
 * El snapshot de 3B se sobreescribe en cada análisis, así que el borrador
 * «actual» de un lead casi nunca es el de una ráfaga pasada. Pintarlo al
 * lado de un veredicto viejo daría una pantalla creíble y falsa. Por eso el
 * texto viene de la propia decisión, y cuando no está la tarjeta lo dice en
 * vez de rellenarlo con el de ahora.
 *
 * Mismo rol que la conversación —admin— porque la comparación que esto
 * existe para permitir es «mensaje real → respuesta del bot», y sin la
 * conversación delante es media comparación.
 */
import React, { useCallback, useEffect, useState } from "react";
import {
  ArrowsClockwise, ChatText, Database, Paperclip, PencilSimple, Robot,
  ThumbsDown, ThumbsUp, WarningCircle,
} from "@phosphor-icons/react";
import { formatError } from "@/lib/api";
import {
  getAutoReplyDecisions, saveAutoReplyFeedback,
} from "@/lib/autoReplyShadowApi";
import {
  ACTION_FEEDBACK_CATEGORIES, FEEDBACK_CATEGORIES, actionCategoryLabel,
  actionLabel, assetSendView, categoryLabel, draftUnavailableText,
  formatBytes, levelLabel, outcomeMeta, pct, reasonList,
} from "@/lib/autoReplyShadow";

const TONE_CLS = {
  ok: "bg-emerald-50 border-emerald-200 text-emerald-700 dark:bg-emerald-950/40 dark:border-emerald-900 dark:text-emerald-300",
  warn: "bg-amber-50 border-amber-200 text-amber-800 dark:bg-amber-950/40 dark:border-amber-900 dark:text-amber-300",
  neutral: "bg-stone-50 border-stone-200 text-stone-600 dark:bg-stone-900 dark:border-stone-700 dark:text-stone-300",
};

const PAGE = 10;

function Chip({ tone = "neutral", cls, children, testid }) {
  return (
    <span
      data-testid={testid}
      className={`inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded border ${cls || TONE_CLS[tone]}`}
    >
      {children}
    </span>
  );
}

function Heading({ children }) {
  return (
    <p className="text-[10px] uppercase tracking-widest text-stone-400 dark:text-stone-500">
      {children}
    </p>
  );
}

function fecha(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("es-ES", {
      day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
    });
  } catch (e) {
    return iso;
  }
}

/** El resumen de arriba. Porcentajes sobre el histórico, no sobre la página. */
function Summary({ summary }) {
  if (!summary || !summary.total) return null;
  const o = summary.outcomes || {};
  // `feedback` cuenta solo veredictos del TEXTO; los de la acción van en
  // `actions`. Pueden fallar por separado, así que se cuentan por separado.
  const f = summary.feedback || {};
  const a = summary.actions || {};
  const filas = [
    ["Habría respondido", pct(o.would_send, summary.total)],
    ["Requiere persona", pct(o.human_required, summary.total)],
    ["No debe responder", pct(o.blocked, summary.total)],
  ];
  return (
    <div
      data-testid="shadow-summary"
      className="rounded border border-stone-200 dark:border-stone-700 p-2 space-y-1"
    >
      <p className="text-sm text-stone-700 dark:text-stone-200">
        {summary.total} {summary.total === 1 ? "decisión" : "decisiones"}
        {summary.truncated ? " (últimas)" : ""}
      </p>
      <div className="flex flex-wrap gap-1">
        {filas.map(([label, valor]) => (
          <Chip key={label}>{label}: {valor === null ? "—" : `${valor}%`}</Chip>
        ))}
      </div>
      {a.planned || a.reviewed ? (
        <div className="flex flex-wrap gap-1" data-testid="shadow-summary-actions">
          {a.planned ? (
            <>
              <Chip>Pidió el dossier: {a.planned}</Chip>
              <Chip tone={a.executable === a.planned ? "ok" : "warn"}>
                Se podía enviar: {a.executable} de {a.planned}
              </Chip>
            </>
          ) : null}
          {a.reviewed ? (
            <Chip tone={a.incorrect ? "warn" : "ok"} testid="shadow-summary-action-review">
              Acción revisada: {a.reviewed} · correctas {a.correct || 0}
              {a.incorrect ? ` · incorrectas ${a.incorrect}` : ""}
            </Chip>
          ) : null}
        </div>
      ) : null}
      {f.reviewed ? (
        <div className="flex flex-wrap gap-1" data-testid="shadow-summary-feedback">
          <Chip tone="ok">
            Correctas: {pct(f.correct, f.reviewed)}%
          </Chip>
          <Chip tone="warn">
            Incorrectas: {pct(f.incorrect, f.reviewed)}%
          </Chip>
          <Chip>Revisadas: {f.reviewed} de {summary.total}</Chip>
        </div>
      ) : (
        <p className="text-[11px] text-stone-400 dark:text-stone-500">
          {a.reviewed
            ? "Todavía nadie ha evaluado el texto de ninguna."
            : "Todavía nadie ha evaluado ninguna."}
        </p>
      )}
      {(summary.top_block_reasons || []).length > 0 && (
        <div className="space-y-0.5">
          <Heading>Motivos más frecuentes</Heading>
          <ul className="text-[11px] text-stone-600 dark:text-stone-300 space-y-0.5">
            {summary.top_block_reasons.map((r) => {
              const texto = reasonList([r.reason])[0];
              if (!texto) return null;
              return (
                <li key={r.reason} className="break-words min-w-0">
                  {texto} · {r.count}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

/** La caja de opinión de una decisión. */
function Feedback({ decision, onSave, busy }) {
  const actual = decision.feedback || null;
  const [abierto, setAbierto] = useState(false);
  const [texto, setTexto] = useState(actual?.corrected_text || "");
  const [categoria, setCategoria] = useState(actual?.category || "");

  const [categoriaAccion, setCategoriaAccion] = useState(actual?.action_category || "");
  const hayAcciones = ((decision.actions || {}).planned || []).length > 0;

  const enviar = (verdict) => onSave(decision.decision_id, { verdict });
  const opinarAccion = (actionVerdict) => onSave(decision.decision_id, {
    actionVerdict,
    actionCategory: actionVerdict === "incorrect" ? (categoriaAccion || null) : null,
  });
  const corregir = () =>
    onSave(decision.decision_id, {
      verdict: "incorrect",
      correctedText: texto,
      category: categoria || null,
    }).then(() => setAbierto(false));

  return (
    <div className="space-y-1 pt-1 border-t border-stone-100 dark:border-stone-800">
      <div className="flex flex-wrap items-center gap-1">
        <button
          type="button"
          disabled={busy}
          data-testid={`shadow-up-${decision.decision_id}`}
          onClick={() => enviar("correct")}
          className={`dv-btn dv-btn-ghost text-xs min-h-[36px] ${actual?.verdict === "correct" ? "text-emerald-700 dark:text-emerald-300" : ""}`}
        >
          <ThumbsUp size={14} /> Correcta
        </button>
        <button
          type="button"
          disabled={busy}
          data-testid={`shadow-down-${decision.decision_id}`}
          onClick={() => enviar("incorrect")}
          className={`dv-btn dv-btn-ghost text-xs min-h-[36px] ${actual?.verdict === "incorrect" ? "text-red-700 dark:text-red-300" : ""}`}
        >
          <ThumbsDown size={14} /> Incorrecta
        </button>
        {decision.draft?.recoverable && (
          <button
            type="button"
            disabled={busy}
            data-testid={`shadow-fix-${decision.decision_id}`}
            onClick={() => setAbierto((v) => !v)}
            className="dv-btn dv-btn-ghost text-xs min-h-[36px]"
          >
            <PencilSimple size={14} /> Corregir respuesta
          </button>
        )}
      </div>

      {hayAcciones && (
        <div className="flex flex-wrap items-center gap-1"
             data-testid={`shadow-action-feedback-${decision.decision_id}`}>
          <span className="text-[11px] text-stone-500 dark:text-stone-400">Acción:</span>
          <button
            type="button"
            disabled={busy}
            data-testid={`shadow-action-up-${decision.decision_id}`}
            onClick={() => opinarAccion("correct")}
            className={`dv-btn dv-btn-ghost text-xs min-h-[36px] ${actual?.action_verdict === "correct" ? "text-emerald-700 dark:text-emerald-300" : ""}`}
          >
            <ThumbsUp size={14} /> Correcta
          </button>
          <button
            type="button"
            disabled={busy}
            data-testid={`shadow-action-down-${decision.decision_id}`}
            onClick={() => opinarAccion("incorrect")}
            className={`dv-btn dv-btn-ghost text-xs min-h-[36px] ${actual?.action_verdict === "incorrect" ? "text-red-700 dark:text-red-300" : ""}`}
          >
            <ThumbsDown size={14} /> Incorrecta
          </button>
          <select
            className="dv-input text-xs"
            value={categoriaAccion}
            data-testid={`shadow-action-cat-${decision.decision_id}`}
            onChange={(e) => setCategoriaAccion(e.target.value)}
          >
            <option value="">Sin categoría</option>
            {ACTION_FEEDBACK_CATEGORIES.map((c) => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
          {actual?.action_verdict && (
            <span className="text-[11px] text-stone-500 dark:text-stone-400"
                  data-testid={`shadow-action-feedback-saved-${decision.decision_id}`}>
              {actual.action_verdict === "correct" ? "Acción correcta" : "Acción incorrecta"}
              {actual.action_category ? ` · ${actionCategoryLabel(actual.action_category)}` : ""}
            </span>
          )}
        </div>
      )}

      {actual?.verdict && !abierto && (
        <p
          className="text-[11px] text-stone-500 dark:text-stone-400 break-words min-w-0"
          data-testid={`shadow-feedback-${decision.decision_id}`}
        >
          {actual.verdict === "correct" ? "Marcada correcta" : "Marcada incorrecta"}
          {actual.category ? ` · ${categoryLabel(actual.category)}` : ""}
          {actual.actor_name ? ` · ${actual.actor_name}` : ""}
          {actual.corrected_text ? ` · con corrección` : ""}
        </p>
      )}

      {abierto && (
        <div className="space-y-1">
          <textarea
            className="dv-input w-full text-sm"
            rows={3}
            value={texto}
            data-testid={`shadow-fix-text-${decision.decision_id}`}
            onChange={(e) => setTexto(e.target.value)}
            placeholder="Lo que debería haber contestado"
          />
          <select
            className="dv-input text-xs"
            value={categoria}
            data-testid={`shadow-fix-cat-${decision.decision_id}`}
            onChange={(e) => setCategoria(e.target.value)}
          >
            <option value="">Sin categoría</option>
            {FEEDBACK_CATEGORIES.map((c) => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
          <div className="flex gap-1">
            <button
              type="button"
              disabled={busy || !texto.trim()}
              data-testid={`shadow-fix-save-${decision.decision_id}`}
              onClick={corregir}
              className="dv-btn dv-btn-primary text-xs min-h-[36px]"
            >
              Guardar corrección
            </button>
            <button
              type="button"
              className="dv-btn dv-btn-ghost text-xs min-h-[36px]"
              onClick={() => setAbierto(false)}
            >
              Cancelar
            </button>
          </div>
          <p className="text-[11px] text-stone-400 dark:text-stone-500">
            Guardar una corrección no envía nada ni cambia el lead.
          </p>
        </div>
      )}
    </div>
  );
}

/**
 * «Acciones que habría ejecutado». Lo que se fijó al decidir —la acción, el
 * fichero, la versión, el tamaño— y si se podía mandar. En condicional: esta
 * pantalla mide, no ejecuta.
 */
function Actions({ decision }) {
  const acciones = decision.actions;
  if (!acciones || !(acciones.planned || []).length) return null;
  const envio = acciones.asset_send || null;
  // El estado de la decisión, corregido por el libro de envíos en vivo.
  const metaEnvio = assetSendView(envio);
  const motivoEnvio = envio ? reasonList([envio.reason])[0] : null;
  return (
    <div className="space-y-0.5" data-testid={`shadow-actions-${decision.decision_id}`}>
      <Heading>Acciones que habría ejecutado</Heading>
      <ul className="space-y-1">
        {acciones.planned.map((accion, i) => {
          const etiqueta = actionLabel(accion);
          if (!etiqueta) return null;
          const activo = (acciones.assets || [])[i] || null;
          const check = (acciones.checks || [])[i] || null;
          const tam = activo ? formatBytes(activo.size_bytes) : null;
          const motivo = check && check.ok !== true ? reasonList([check.reason])[0] : null;
          return (
            <li key={`${accion.type}-${i}`} className="text-sm min-w-0"
                data-testid={`shadow-action-${decision.decision_id}-${i}`}>
              <p className="text-stone-800 dark:text-stone-100 break-words min-w-0">
                <Paperclip size={13} className="inline mr-1" />
                {etiqueta}
              </p>
              {activo ? (
                <p className="text-[11px] text-stone-500 dark:text-stone-400 break-words min-w-0">
                  {activo.filename}
                  {activo.version ? ` · v${activo.version}` : ""}
                  {tam ? ` · ${tam}` : ""}
                </p>
              ) : (
                <p className="text-[11px] text-stone-500 dark:text-stone-400">
                  Sin documento resuelto.
                </p>
              )}
              <div className="flex flex-wrap gap-1 pt-0.5">
                {check?.ok === true ? (
                  <Chip tone="ok">Se podía enviar</Chip>
                ) : (
                  <Chip tone="warn" testid={`shadow-action-blocked-${decision.decision_id}-${i}`}>
                    No se podía enviar{motivo ? `: ${motivo}` : ""}
                  </Chip>
                )}
              </div>
            </li>
          );
        })}
      </ul>
      {metaEnvio && (
        <div className="flex flex-wrap items-center gap-1 pt-0.5 min-w-0"
             data-testid={`shadow-asset-send-${decision.decision_id}`}>
          <Chip tone={metaEnvio.tone}
                testid={`shadow-asset-send-state-${decision.decision_id}`}>
            Documento: {metaEnvio.label}
          </Chip>
          {motivoEnvio && (
            <span className="text-[11px] text-stone-500 dark:text-stone-400 break-words min-w-0">
              {motivoEnvio}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function Decision({ decision, onSave, busy }) {
  const meta = outcomeMeta(decision.outcome);
  const motivos = reasonList(decision.reasons);
  const nivel = levelLabel(decision.level);
  const cobertura = decision.coverage || {};
  const borrador = decision.draft || {};
  const entrante = decision.inbound || {};
  const hechos = (decision.knowledge || {}).used_fact_ids || [];

  return (
    <li
      className="space-y-1.5 py-2 border-t border-stone-200 dark:border-stone-700 min-w-0"
      data-testid={`shadow-decision-${decision.decision_id}`}
    >
      <div className="flex flex-wrap items-center gap-1">
        {meta ? (
          <Chip cls={meta.cls} testid={`shadow-outcome-${decision.decision_id}`}>
            {meta.label}
          </Chip>
        ) : null}
        {nivel && <Chip>{nivel}</Chip>}
        <span className="text-[11px] text-stone-400 dark:text-stone-500">
          {fecha(decision.created_at)}
        </span>
      </div>

      {meta?.hint && (
        <p className="text-[11px] text-stone-500 dark:text-stone-400">{meta.hint}</p>
      )}

      {/* Lo que escribió el lead. Sin esto no hay comparación. */}
      {entrante.first?.text ? (
        <div className="space-y-0.5">
          <Heading>
            <ChatText size={11} className="inline mr-1" />
            {entrante.count > 1
              ? `El lead escribió ${entrante.count} mensajes`
              : "El lead escribió"}
          </Heading>
          <p className="text-sm text-stone-700 dark:text-stone-200 break-words min-w-0">
            {entrante.first.text}
          </p>
          {entrante.count > 1 && entrante.last?.text
            && entrante.last.text !== entrante.first.text && (
            <p className="text-sm text-stone-700 dark:text-stone-200 break-words min-w-0">
              … {entrante.last.text}
            </p>
          )}
        </div>
      ) : null}

      {/* Lo que habría contestado el bot. */}
      <div className="space-y-0.5">
        <Heading>
          <Robot size={11} className="inline mr-1" /> El bot habría respondido
        </Heading>
        {borrador.recoverable ? (
          <>
            <p
              className="text-sm text-stone-800 dark:text-stone-100 break-words min-w-0 whitespace-pre-wrap"
              data-testid={`shadow-draft-${decision.decision_id}`}
            >
              {borrador.text}
            </p>
            {borrador.matches_current_analysis === false && (
              <p className="text-[11px] text-stone-400 dark:text-stone-500">
                El análisis de este lead ha cambiado desde entonces.
              </p>
            )}
          </>
        ) : (
          <>
            <p
              className="text-sm text-stone-500 dark:text-stone-400 break-words min-w-0"
              data-testid={`shadow-nodraft-${decision.decision_id}`}
            >
              {draftUnavailableText(borrador.reason)}
            </p>
            {/* El motivo, aquí mismo. Saber que no hay borrador sin saber
                por qué obliga a bajar la vista para entender la fila. */}
            {motivos.length > 0 && (
              <p className="text-[11px] text-stone-500 dark:text-stone-400 break-words min-w-0">
                Motivo: {motivos[0]}.
              </p>
            )}
          </>
        )}
      </div>

      <Actions decision={decision} />

      {motivos.length > 0 && (
        <div className="space-y-0.5">
          <Heading>Por qué</Heading>
          <ul className="text-[11px] text-stone-600 dark:text-stone-300 space-y-0.5">
            {motivos.map((m) => (
              <li key={m} className="break-words min-w-0">· {m}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Los HECHOS, con su referencia entera. Las categorías de abajo
          contestan otra pregunta —si cada afirmación tenía citada su
          familia— y con ellas no se puede ir al dato. */}
      {hechos.length > 0 && (
        <div className="space-y-0.5">
          <Heading>
            <Database size={11} className="inline mr-1" /> Se apoyó en
          </Heading>
          <ul
            className="text-[11px] text-stone-600 dark:text-stone-300 space-y-0.5"
            data-testid={`shadow-facts-${decision.decision_id}`}
          >
            {hechos.map((f) => (
              <li key={f} className="break-words min-w-0 font-mono">{f}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex flex-wrap gap-1">
        {cobertura.verified === true && (
          <Chip tone="ok">Todo lo que dice está respaldado</Chip>
        )}
        {cobertura.verified === false && (
          <Chip tone="warn">No se puede demostrar que todo esté respaldado</Chip>
        )}
        {(cobertura.cited || []).map((c) => (
          <Chip key={c} testid={`shadow-cited-${decision.decision_id}-${c}`}>
            {c}
          </Chip>
        ))}
      </div>

      {decision.send?.state && (
        <p className="text-[11px] text-stone-500 dark:text-stone-400">
          Envío: {outcomeMeta(decision.send.state)?.label || decision.send.state}
          {reasonList([decision.send.reason]).length
            ? ` · ${reasonList([decision.send.reason])[0]}`
            : ""}
        </p>
      )}

      <p className="text-[10px] text-stone-400 dark:text-stone-500 break-words min-w-0">
        {decision.versions?.policy} · {decision.versions?.auto_reply}
      </p>

      <Feedback decision={decision} onSave={onSave} busy={busy} />
    </li>
  );
}

export default function LeadAutoReplyShadowCard({ leadId }) {
  const [state, setState] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getAutoReplyDecisions(leadId, { limit: PAGE });
      setState(data);
      setItems(data.items);
    } catch (e) {
      setError(formatError(e));
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => { load(); }, [load]);

  const loadOlder = async () => {
    if (!state?.next_before) return;
    setBusy(true);
    setActionError(null);
    try {
      const data = await getAutoReplyDecisions(leadId, {
        limit: PAGE, before: state.next_before,
      });
      setItems((prev) => [...prev, ...data.items]);
      setState((prev) => ({ ...prev, next_before: data.next_before }));
    } catch (e) {
      setActionError(formatError(e));
    } finally {
      setBusy(false);
    }
  };

  const onSave = async (decisionId, payload) => {
    setBusy(true);
    setActionError(null);
    try {
      const saved = await saveAutoReplyFeedback(leadId, decisionId, payload);
      // Se pinta lo que devolvió la respuesta en vez de recargar la página
      // entera: el GET vuelve a contar el resumen sobre todo el histórico.
      setItems((prev) => prev.map((d) => (
        d.decision_id === decisionId ? { ...d, feedback: saved } : d
      )));
    } catch (e) {
      setActionError(formatError(e));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="dv-card text-xs text-stone-400" data-testid="shadow-loading">
        Cargando decisiones…
      </div>
    );
  }

  if (error) {
    return (
      <div className="dv-card space-y-2">
        <p className="text-xs text-red-600" data-testid="shadow-error">{error}</p>
        <button type="button" className="dv-btn dv-btn-ghost text-xs" onClick={load}>
          <ArrowsClockwise size={14} /> Reintentar
        </button>
      </div>
    );
  }

  return (
    <div className="dv-card space-y-2">
      {state && state.shadow_enabled === false && (
        <p className="text-[11px] text-stone-400 dark:text-stone-500"
           data-testid="shadow-off">
          Auto Reply está apagado. Esto es el histórico.
        </p>
      )}

      <Summary summary={state?.summary} />

      {items.length === 0 ? (
        <p className="text-sm text-stone-600 dark:text-stone-300"
           data-testid="shadow-empty">
          Todavía no se ha procesado ninguna ráfaga de este lead.
        </p>
      ) : (
        <ul className="space-y-0">
          {items.map((d) => (
            <Decision key={d.decision_id} decision={d} onSave={onSave}
                      busy={busy} />
          ))}
        </ul>
      )}

      {actionError && (
        <p className="text-xs text-red-600 flex items-center gap-1"
           data-testid="shadow-action-error">
          <WarningCircle size={14} /> {actionError}
        </p>
      )}

      {state?.next_before && (
        <button type="button" disabled={busy} onClick={loadOlder}
                data-testid="shadow-older"
                className="dv-btn dv-btn-ghost text-xs">
          Ver decisiones anteriores
        </button>
      )}

      {/* Permanente, y no solo dentro de la caja de corrección: quien
          pulsa un pulgar tiene que saber que no está aprobando un envío.
          Hay un test que exige que esta frase esté. */}
      <p className="text-[11px] text-stone-400 dark:text-stone-500">
        Evaluar no envía nada ni enciende el envío automático.
      </p>
    </div>
  );
}
